import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Computer,
  ContentCopy,
  Lock,
} from '@mui/icons-material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { customFetcher } from '../hooks/useFetch';
import { prettyClientName } from '../core/clientNames';
import { useGetSettings } from '../hooks/reactQuerys/SettingQueries';

interface PendingAuth {
  userCode: string;
  ClientName: string;
  Status: 'pending' | 'approved' | 'denied' | 'expired';
  IP?: string | null;
  UserAgent?: string | null;
  Created_at?: string;
  Expires_at?: string;
}

type Stage =
  | 'loading'
  | 'pending'
  | 'approved'
  | 'denied'
  | 'expired'
  | 'not-found'
  | 'error';

/**
 * Authorize-Page für den OAuth-Device-Flow (RFC 8628):
 * Plugins schicken den User auf `/authorize?code=<UserCode>`. Der bereits im
 * Web-Frontend eingeloggte User sieht hier, welches Plugin Zugriff möchte,
 * und kann „Erlauben" oder „Ablehnen" wählen. Das Plugin pollt im
 * Hintergrund und holt sich nach Approve den ausgestellten SessionToken ab.
 *
 * Wenn die URL ohne `?code=` aufgerufen wird, kann der User den Code aus
 * dem Plugin-Display manuell eingeben.
 *
 * Fuer Apps (DiNo Mitarbeiter-App) steht zusaetzlich die Serveradresse als
 * QR-Code bereit. Eine App kennt den Server naemlich noch nicht, wenn sie sich
 * zum ersten Mal verbindet — ohne Adresse kann sie gar keinen Gerätecode
 * anfordern. Der QR-Code ist bewusst nur eine **Abkuerzung**: die Adresse steht
 * daneben als Text, und der Code laesst sich weiterhin von Hand eintippen. Eine
 * IP fehlerfrei abzutippen ist die haeufigste Fehlerquelle bei der Einrichtung.
 */
export const Authorize: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { settings } = useGetSettings();

  /**
   * Basis-Adresse dieser DiNo-Instanz aus der Einstellung `Private.Link.DiNo` —
   * dieselbe Quelle, aus der die Tablet-Kopplung ihren QR-Code speist.
   *
   * Bewusst der rohe Wert **ohne** `/api`: es ist genau die Adresse, die man
   * sonst in der App eintippt. Den API-Pfad haengt die App selbst an.
   */
  const serverUrl = (
    settings?.find(setting => setting.SettingKey === 'Private.Link.DiNo')
      ?.SettingValue ?? ''
  ).trim();

  const initialCode = (searchParams.get('code') || '').trim().toUpperCase();

  /**
   * Aufruf aus „Mein Konto → Geräte". Dann fuehrt der Abschluss dorthin
   * zurueck statt auf die Startseite — die Geraeteliste laedt beim Betreten
   * neu, das erledigte vorher der eigene Dialog.
   */
  const cameFromAccount = searchParams.get('from') === 'account';
  const backPath = cameFromAccount ? '/account' : '/';
  const backLabel = cameFromAccount
    ? 'Zurück zu „Mein Konto"'
    : 'Zurück zur Übersicht';
  const [code, setCode] = useState(initialCode);
  const [pending, setPending] = useState<PendingAuth | null>(null);
  const [stage, setStage] = useState<Stage>(
    initialCode ? 'loading' : 'pending'
  );
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchPending = useCallback(async (userCode: string) => {
    setStage('loading');
    setErrorMsg(null);
    try {
      const data = (await customFetcher({
        endpoint: `/oauth/device/${encodeURIComponent(userCode)}`,
      })) as PendingAuth;
      setPending(data);
      if (data.Status === 'approved') setStage('approved');
      else if (data.Status === 'denied') setStage('denied');
      else if (data.Status === 'expired') setStage('expired');
      else setStage('pending');
    } catch (e) {
      // 404 → unbekannt/abgelaufen, anderes → echter Fehler
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('Network response was not ok')) {
        setStage('not-found');
      } else {
        setErrorMsg(msg);
        setStage('error');
      }
    }
  }, []);

  useEffect(() => {
    if (initialCode) void fetchPending(initialCode);
  }, [initialCode, fetchPending]);

  const handleApprove = async () => {
    if (!code) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      await customFetcher({
        endpoint: `/oauth/device/${encodeURIComponent(code)}/approve`,
        method: 'POST',
      });
      setStage('approved');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Approve fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleDeny = async () => {
    if (!code) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      await customFetcher({
        endpoint: `/oauth/device/${encodeURIComponent(code)}/deny`,
        method: 'POST',
      });
      setStage('denied');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Deny fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleManualLookup = () => {
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) return;
    void fetchPending(cleaned);
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 96px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        sx={{
          maxWidth: 520,
          width: '100%',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Lock color='primary' />
          <Typography variant='h6'>Plugin-Zugriff bestätigen</Typography>
        </Stack>

        {stage === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {(stage === 'pending' && !pending) || stage === 'not-found' ? (
          <>
            <Typography variant='body2' color='text.secondary'>
              {stage === 'not-found'
                ? 'Der eingegebene Code ist unbekannt oder abgelaufen.'
                : 'Bitte geben Sie den im Plugin angezeigten Code ein:'}
            </Typography>
            <TextField
              label='Code'
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder='K7M4-9XQR'
              autoFocus
              inputProps={{
                style: { letterSpacing: 2, fontFamily: 'monospace' },
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') handleManualLookup();
              }}
            />
            <Button
              variant='contained'
              onClick={handleManualLookup}
              disabled={!code.trim()}
            >
              Code prüfen
            </Button>

            {/* Serveradresse fuer Apps. Steht bewusst hier: an dieser Stelle
                sitzt jemand vor der Einrichtung eines Geraets. */}
            <Box sx={{ pt: 1 }}>
              <Typography variant='subtitle2'>
                Adresse für die App
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Eine App muss zuerst wissen, wo dieses DiNo erreichbar ist.
                Scannen Sie den Code — oder tippen Sie die Adresse ein, das
                bleibt jederzeit möglich.
              </Typography>

              {serverUrl === '' ? (
                <Alert severity='warning' sx={{ mt: 1 }}>
                  Die Einstellung <b>Private.Link.DiNo</b> ist leer. Bitte erst
                  unter Einstellungen die Adresse dieser Instanz hinterlegen —
                  im Netz des Notariats in der Regel die IP-Adresse, etwa{' '}
                  <code>https://192.168.0.10:8080</code>. Solange sie fehlt,
                  muss die Adresse in der App von Hand eingegeben werden.
                </Alert>
              ) : (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems='center'
                  sx={{ mt: 1 }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'common.white',
                      borderRadius: 2,
                      width: 168,
                      height: 168,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <QRCode
                      value={JSON.stringify({ u: serverUrl })}
                      size={144}
                      level='M'
                      style={{ height: 'auto', width: '100%' }}
                    />
                  </Box>

                  <Stack spacing={1} sx={{ minWidth: 0, width: '100%' }}>
                    <TextField
                      label='Adresse'
                      value={serverUrl}
                      size='small'
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText='Zum Abtippen oder Kopieren.'
                    />
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<ContentCopy />}
                      onClick={() =>
                        navigator.clipboard?.writeText(serverUrl)
                      }
                    >
                      Adresse kopieren
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Box>
          </>
        ) : null}

        {pending && stage === 'pending' && (
          <>
            <Alert severity='info' icon={<Computer />}>
              <strong>{prettyClientName(pending.ClientName)}</strong> möchte auf
              Ihr DiNo-Konto zugreifen.
            </Alert>

            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ minWidth: 90 }}
                >
                  Code
                </Typography>
                <Chip
                  label={pending.userCode}
                  variant='outlined'
                  sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
                  size='small'
                />
                <Button
                  size='small'
                  startIcon={<ContentCopy fontSize='inherit' />}
                  onClick={() =>
                    void navigator.clipboard.writeText(pending.userCode)
                  }
                >
                  Kopieren
                </Button>
              </Stack>
              {pending.IP && (
                <Stack direction='row' spacing={1}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ minWidth: 90 }}
                  >
                    IP
                  </Typography>
                  <Typography variant='caption'>{pending.IP}</Typography>
                </Stack>
              )}
              {pending.UserAgent && (
                <Stack direction='row' spacing={1}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ minWidth: 90 }}
                  >
                    Client
                  </Typography>
                  <Typography variant='caption' sx={{ wordBreak: 'break-all' }}>
                    {pending.UserAgent}
                  </Typography>
                </Stack>
              )}
              {pending.Expires_at && (
                <Stack direction='row' spacing={1}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ minWidth: 90 }}
                  >
                    Gültig bis
                  </Typography>
                  <Typography variant='caption'>
                    {new Date(pending.Expires_at).toLocaleString('de-DE')}
                  </Typography>
                </Stack>
              )}
            </Box>

            <Typography variant='body2'>
              Bitte nur bestätigen, wenn Sie diese Anfrage soeben in Ihrem
              Plugin ausgelöst haben. Nach dem Bestätigen kann das Plugin in
              Ihrem Namen auf DiNo zugreifen, bis Sie die Verbindung wieder
              widerrufen.
            </Typography>

            {errorMsg && <Alert severity='error'>{errorMsg}</Alert>}

            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button
                onClick={handleDeny}
                disabled={busy}
                startIcon={<Close />}
                color='inherit'
              >
                Ablehnen
              </Button>
              <Button
                onClick={handleApprove}
                disabled={busy}
                variant='contained'
                startIcon={<CheckCircle />}
              >
                {busy ? 'Speichere…' : 'Erlauben'}
              </Button>
            </Stack>
          </>
        )}

        {stage === 'approved' && (
          <>
            <Alert severity='success' icon={<CheckCircle />}>
              Plugin-Zugriff erlaubt. Sie können dieses Fenster schließen — das
              Plugin holt sich den Token automatisch.
            </Alert>
            <Button onClick={() => navigate(backPath)}>{backLabel}</Button>
          </>
        )}

        {stage === 'denied' && (
          <>
            <Alert severity='warning' icon={<Close />}>
              Sie haben den Plugin-Zugriff abgelehnt. Das Plugin wird beim
              nächsten Polling-Versuch eine entsprechende Antwort erhalten.
            </Alert>
            <Button onClick={() => navigate(backPath)}>{backLabel}</Button>
          </>
        )}

        {stage === 'expired' && (
          <Alert severity='warning'>
            Die Anfrage ist abgelaufen. Bitte starten Sie den
            Verbindungs-Vorgang im Plugin neu.
          </Alert>
        )}

        {stage === 'error' && (
          <Alert severity='error'>
            {errorMsg || 'Es ist ein unbekannter Fehler aufgetreten.'}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};
